import { API_URL } from './constants';
import { debugLog, debugError } from './utils';

export async function callAPI(conversation, personalityType, onChunk) {
  try {
    debugLog('Sending conversation:', conversation);
    debugLog('Using personality:', personalityType);

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      credentials: 'omit',
      body: JSON.stringify({
        conversation,
        personalityType // Include personality in request body
      }),
    });

    debugLog('Response received:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      const errorText = await response.text();
      debugError('Error response:', errorText);

      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(`Server error: ${errorJson.details || errorJson.error}`);
      } catch (e) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    if (!response.body) {
      throw new Error('No readable stream available');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let buffer = '';
    let fullMessage = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          debugLog('Stream complete');
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        // debugLog('Raw chunk received:', chunk);

        // If we receive a JSON error response instead of SSE
        if (chunk.includes('"error":')) {
          try {
            const errorResponse = JSON.parse(chunk);
            throw new Error(errorResponse.details || errorResponse.error);
          } catch (e) {
            debugError('Error parsing error response:', e);
          }
        }

        buffer += chunk;

        // Process all complete lines in the buffer
        while (buffer.includes('\n')) {

          const lineEnd = buffer.indexOf('\n');
          const line = buffer.slice(0, lineEnd).trim();
          buffer = buffer.slice(lineEnd + 1);

          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            if (data === '[DONE]') {
              debugLog('Received DONE signal');
              return fullMessage;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'content' && parsed.content) {
                fullMessage += parsed.content;
                onChunk(parsed.content);
              } else if (parsed.type === 'metadata') {
                // Handle metadata (like personality confirmation) if needed
                debugLog('Received metadata:', parsed);
              } else if (parsed.error) {
                // Handle potential error events sent via SSE
                debugError("Received error via SSE:", parsed.details || parsed.error);
                throw new Error(parsed.details || parsed.error);
              }
            } catch (e) {
              debugError('Error parsing line:', line, e);
            }
          }
        }
      }
    } catch (error) {
      debugError('Error reading stream:', error);
      throw error;
    } finally {
      reader.releaseLock();
    }

    return fullMessage;
  } catch (error) {
    debugError('API call failed:', error);
    throw error;
  }
}

export async function getSuggestions(text) {
  if (!text) return [];
  try {
    const response = await fetch(
      `https://api.datamuse.com/sug?s=${encodeURIComponent(text)}&max=5`);
    const data = await response.json();
    return data ? data : [];
  } catch (error) {
    debugError('Error fetching suggestions:', error);
    return [];
  }
}